import React, { useEffect, useRef, useMemo, useState } from "react";
import { Col, Card, Dropdown } from "react-bootstrap";
import { useDonor } from "../../../../../context/donor/donor.context";
import { useLanguage } from "../../../../../context/language/LanguageContext";
import { useFilter } from "../../../../../context/filter/FilterContext";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { arSA, enUS } from "date-fns/locale";
import DonorCountPerCampaignBarChart from "./DonorCountPerCampaignBarChart";

const LIMIT_OPTIONS = [
  { value: 0, label: "All" },
  { value: 5, label: "5" },
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const TIME_PERIOD_OPTIONS = [
  { value: "", labelKey: "All" },
  { value: "DAY", labelKey: "Day" },
  { value: "MONTH", labelKey: "Month" },
  { value: "SIX_MONTHS", labelKey: "Six Months" },
  { value: "YEAR", labelKey: "Year" },
];

const DonorCountPerCampaignChartWrapper = () => {
  const {
    donorCountPerCollectionSite,
    fetchDonorCountPerCollectionSite,
    collectionSites,
    fetchAllCollectionSites,
    loading
  } = useDonor();
  const { language } = useLanguage();
  const { filters, updateFilter } = useFilter();
  const { t } = useTranslation();
  const hasFetchedRef = useRef(false);
  const dateLocale = language === "ar" ? arSA : enUS;

  const [limit, setLimit] = useState(0);
  const [selectedCollectionSiteId, setSelectedCollectionSiteId] = useState("");
  const [timePeriod, setTimePeriod] = useState("");

  // Get date range from filter context
  const dateRange = filters.dateRange;
  const hasDateFilter = dateRange.start && dateRange.end;

  // Format date range for display
  const getDateRangeDisplay = () => {
    if (!hasDateFilter) return null;
    const startFormatted = format(dateRange.start, "dd MMMM yyyy", { locale: dateLocale });
    const endFormatted = format(dateRange.end, "dd MMMM yyyy", { locale: dateLocale });
    return `${startFormatted} → ${endFormatted}`;
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      const locale = language || "en";
      fetchDonorCountPerCollectionSite({ lang: locale, limit });
      fetchAllCollectionSites();
      hasFetchedRef.current = true;
    }
  }, []);

  const buildFilter = (overrides = {}) => {
    const filter = { lang: language, limit, ...overrides };
    const siteId = overrides.campaignId !== undefined ? overrides.campaignId : selectedCollectionSiteId;
    const period = overrides.timePeriod !== undefined ? overrides.timePeriod : timePeriod;

    if (siteId) {
      filter.campaignId = siteId;
    }
    if (period) {
      filter.timePeriod = period;
    }
    // Include date filter from global filter context
    if (hasDateFilter) {
      filter.startDate = dateRange.start.toISOString();
      filter.endDate = dateRange.end.toISOString();
    }
    return filter;
  };

  const handleRefresh = () => {
    fetchDonorCountPerCollectionSite(buildFilter());
  };

  const handleClearDateFilter = () => {
    // Clear date filter from context
    updateFilter("dateRange", { start: null, end: null });
    // Refetch without date filter
    const filter = { lang: language, limit };
    if (selectedCollectionSiteId) {
      filter.campaignId = selectedCollectionSiteId;
    }
    if (timePeriod) {
      filter.timePeriod = timePeriod;
    }
    fetchDonorCountPerCollectionSite(filter);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    fetchDonorCountPerCollectionSite(buildFilter({ limit: newLimit }));
  };

  const handleCollectionSiteChange = (collectionSiteId) => {
    setSelectedCollectionSiteId(collectionSiteId);
    fetchDonorCountPerCollectionSite(buildFilter({ campaignId: collectionSiteId }));
  };

  const handleTimePeriodChange = (newTimePeriod) => {
    setTimePeriod(newTimePeriod);
    fetchDonorCountPerCollectionSite(buildFilter({ timePeriod: newTimePeriod }));
  };

  const getLimitLabel = () => {
    const option = LIMIT_OPTIONS.find(opt => opt.value === limit);
    return option ? (option.value === 0 ? t("All") : option.label) : t("All");
  };

  const getTimePeriodLabel = () => {
    const option = TIME_PERIOD_OPTIONS.find(opt => opt.value === timePeriod);
    return option ? t(option.labelKey) : t("All");
  };

  const getCollectionSiteDisplayName = (site) => {
    return language === "ar" ? site.title_ar : site.title_en;
  };

  const getSelectedCollectionSiteLabel = () => {
    if (!selectedCollectionSiteId) return t("All");
    const site = collectionSites.find(s => s.id === selectedCollectionSiteId);
    return site ? getCollectionSiteDisplayName(site) : t("All");
  };

  const totalDonors = useMemo(() => {
    if (!donorCountPerCollectionSite || !Array.isArray(donorCountPerCollectionSite)) return 0;
    return donorCountPerCollectionSite.reduce((sum, item) => sum + item.donorCount, 0);
  }, [donorCountPerCollectionSite]);

  return (
    <Col xl={12} lg={12}>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "12px" }}>
          <div>
            <Card.Title className="mb-1">{t("Donor Count Per Collection Site")}</Card.Title>
            {hasDateFilter && (
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <i className="fa fa-calendar-alt text-primary" style={{ fontSize: "12px" }}></i>
                <span className="text-muted" style={{ fontSize: "12px" }}>
                  {getDateRangeDisplay()}
                </span>
                <button
                  className="btn btn-link p-0"
                  onClick={handleClearDateFilter}
                  style={{ fontSize: "12px", color: "#dc3545" }}
                  title={t("Clear date filter")}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            )}
          </div>
          <div className="d-flex align-items-end" style={{ gap: "12px" }}>
            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <span className="text-muted" style={{ fontSize: "12px" }}>{t("Collection Site")}</span>
              <Dropdown className="dropdown bootstrap-select form-control style-1 default-select" style={{ width: "auto", padding: 0, minWidth: "250px" }}>
                <Dropdown.Toggle
                  variant=""
                  className="btn dropdown-toggle btn-light btn-sm"
                  disabled={loading}
                  style={{ width: "100%", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {getSelectedCollectionSiteLabel()}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      { name: "flip", enabled: false },
                      { name: "preventOverflow", options: { boundary: "viewport" } }
                    ]
                  }}
                  style={{ maxHeight: "300px", overflowY: "auto", minWidth: "300px" }}
                >
                  <Dropdown.Item
                    onClick={() => handleCollectionSiteChange("")}
                    active={!selectedCollectionSiteId}
                  >
                    {t("All")}
                  </Dropdown.Item>
                  {collectionSites.map((site) => (
                    <Dropdown.Item
                      key={site.id}
                      onClick={() => handleCollectionSiteChange(site.id)}
                      active={selectedCollectionSiteId === site.id}
                      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {getCollectionSiteDisplayName(site)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <span className="text-muted" style={{ fontSize: "12px" }}>{t("Time Period")}</span>
              <Dropdown className="dropdown bootstrap-select form-control style-1 default-select" style={{ width: "auto", padding: 0 }}>
                <Dropdown.Toggle
                  variant=""
                  className="btn dropdown-toggle btn-light btn-sm"
                  disabled={loading}
                >
                  {getTimePeriodLabel()}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      { name: "flip", enabled: false },
                      { name: "preventOverflow", options: { boundary: "viewport" } }
                    ]
                  }}
                >
                  {TIME_PERIOD_OPTIONS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      onClick={() => handleTimePeriodChange(option.value)}
                      active={timePeriod === option.value}
                    >
                      {t(option.labelKey)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <span className="text-muted" style={{ fontSize: "12px" }}>{t("Limit")}</span>
              <Dropdown className="dropdown bootstrap-select form-control style-1 default-select" style={{ width: "auto", padding: 0 }}>
                <Dropdown.Toggle
                  variant=""
                  className="btn dropdown-toggle btn-light btn-sm"
                  disabled={loading}
                >
                  {getLimitLabel()}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  popperConfig={{
                    modifiers: [
                      { name: "flip", enabled: false },
                      { name: "preventOverflow", options: { boundary: "viewport" } }
                    ]
                  }}
                >
                  {LIMIT_OPTIONS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      onClick={() => handleLimitChange(option.value)}
                      active={limit === option.value}
                    >
                      {option.value === 0 ? t("All") : option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  {t("Loading")}...
                </>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"
                  />
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                </svg>
              )}
            </button>
          </div>
        </Card.Header>

        <Card.Body>
          {loading && !donorCountPerCollectionSite ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">{t("Loading")}...</span>
              </div>
            </div>
          ) : donorCountPerCollectionSite && donorCountPerCollectionSite.length > 0 ? (
            <>
              <DonorCountPerCampaignBarChart data={donorCountPerCollectionSite} />
              <div className="mt-3 text-center">
                <div className="row">
                  <div className="col-md-6">
                    <strong>{t("Total Collection Sites")}: </strong>{donorCountPerCollectionSite.length}
                  </div>
                  <div className="col-md-6">
                    <strong>{t("donors.totalDonors")}: </strong>{totalDonors}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <p>{t("No data available")}</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default DonorCountPerCampaignChartWrapper;
